import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EarlyAccessForm({ compact = false }) {
  const [form, setForm] = useState({ name: "", email: "", experience: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Interest registered. We'll be in touch.");
      setDone(true);
      setForm({ name: "", email: "", experience: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Could not submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className={`space-y-4 text-left ${compact ? "" : "mt-10"}`} data-testid="lead-form">
      <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="lead-name" />
      <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="lead-email" />
      <label className="block">
        <div className="label-xs mb-2">Market Experience</div>
        <select
          required
          value={form.experience}
          onChange={(e) => setForm({ ...form, experience: e.target.value })}
          data-testid="lead-experience"
          style={{ colorScheme: "dark" }}
          className="w-full bg-[#0E1014] border border-white/10 rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-ai-accent/60 transition-colors"
        >
          <option value="" className="bg-[#08090B] text-white">Select…</option>
          <option className="bg-[#08090B] text-white">New to markets (&lt; 1 year)</option>
          <option className="bg-[#08090B] text-white">Intermediate (1–3 years)</option>
          <option className="bg-[#08090B] text-white">Experienced (3–7 years)</option>
          <option className="bg-[#08090B] text-white">Professional / Institutional</option>
        </select>
      </label>
      <button
        type="submit"
        disabled={loading || done}
        data-testid="lead-submit"
        className="w-full mt-2 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md brand-gradient text-white font-medium hover:brightness-110 transition-all disabled:opacity-60 shadow-[0_0_40px_-12px_rgba(0,184,255,0.6)]"
      >
        {loading ? "Submitting…" : done ? "Interest registered ✓" : <>Register Interest <ArrowRight className="w-4 h-4" /></>}
      </button>
      <p data-testid="lead-form-note" className="text-[11px] text-zinc-500 leading-relaxed">
        This form only registers your interest in future platform access and collects basic contact information.
        It is not a purchase, subscription, or any form of investment or advisory service, and no payment is collected.
      </p>
    </form>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <label className="block">
      <div className="label-xs mb-2">{label}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full bg-[#0E1014] border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-ai-accent/60 transition-colors"
      />
    </label>
  );
}
